import React, { useEffect, useState } from 'react';
import { fetchJson } from '../../store/fetch';
import { openApiFetchGet, openApiFetchPost } from '../../store/openApiFetch';
import { Content, Panel, PanelBody, TopbarCrumbActive, TopbarLeftOnly, Tray } from '../elements';
import { Spinner } from '../spinners';

interface IClientOption {
    value: string;
    text: string;
}

interface IPreviewCounts {
    total: number;
    alreadyEmbedded: number;
    toProcess: number;
}

interface IPreview {
    assets: IPreviewCounts;
    surveys: IPreviewCounts;
}

interface IStartResult {
    assetsQueued: number;
    surveysQueued: number;
    assetsSkipped: number;
    surveysSkipped: number;
}

interface IQueueStats {
    pending: number;
    active: number;
    retry: number;
}

function toInputDate(date: Date): string {
    return date.toISOString().substring(0, 10);
}

function firstDayOfThisYear(): string {
    const d = new Date();
    return toInputDate(new Date(d.getFullYear(), 0, 1));
}

function today(): string {
    return toInputDate(new Date());
}

function CountsBlock({ title, counts }: { title: string; counts: IPreviewCounts }) {
    return (
        <div style={{ display: 'inline-block', marginRight: 20, padding: '10px 16px', border: '1px solid #ddd', borderRadius: 4 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{title}</div>
            <div style={{ fontSize: 12, color: '#666' }}>Totaal: {counts.total}</div>
            <div style={{ fontSize: 12, color: '#666' }}>Al geëmbed: {counts.alreadyEmbedded}</div>
            <div style={{ fontSize: 14, fontWeight: 700, marginTop: 4 }}>Te verwerken: {counts.toProcess}</div>
        </div>
    );
}

export function AiBackfill() {
    const [clients, setClients] = useState<IClientOption[]>([]);
    const [clientCompanyId, setClientCompanyId] = useState<string>('');
    const [startDate, setStartDate] = useState<string>(firstDayOfThisYear());
    const [endDate, setEndDate] = useState<string>(today());
    const [shouldIncludeAssets, setShouldIncludeAssets] = useState(true);
    const [shouldIncludeSurveys, setShouldIncludeSurveys] = useState(true);
    const [preview, setPreview] = useState<IPreview | null>(null);
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [isStarting, setIsStarting] = useState(false);
    const [lastStartResult, setLastStartResult] = useState<IStartResult | null>(null);
    const [queueStats, setQueueStats] = useState<IQueueStats | null>(null);
    const [isClearing, setIsClearing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        void (async () => {
            try {
                const response = (await fetchJson('/api/clients', 'GET')) as { json?: { items?: { id: string; name: string }[] } };
                const items = response.json?.items ?? [];
                setClients(items.map((c) => ({ value: c.id, text: c.name })));
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Kon clients niet laden');
            }
        })();
    }, []);

    function canRun(): boolean {
        return !!clientCompanyId && !!startDate && !!endDate && new Date(startDate) <= new Date(endDate);
    }

    async function refreshQueueStats(forClientId: string): Promise<void> {
        if (!forClientId) {
            setQueueStats(null);
            return;
        }
        try {
            const result = await openApiFetchGet('/api/ai/backfill/pending', { clientCompanyId: forClientId });
            setQueueStats(result);
        } catch {
            // Non-fatal — the rest of the UI still works.
            setQueueStats(null);
        }
    }

    // Refresh queue stats whenever the selected client changes, and once
    // after a start/cancel action so the numbers track recent activity.
    useEffect(() => {
        void refreshQueueStats(clientCompanyId);
    }, [clientCompanyId]);

    async function runCancelPending() {
        if (!clientCompanyId || !queueStats) {
            return;
        }
        const total = queueStats.pending + queueStats.retry;
        if (total === 0) {
            return;
        }
        const isConfirmed = window.confirm(
            `Weet je zeker dat je ${total} wachtende embed-job${total === 1 ? '' : 's'} wilt annuleren?\n\nDit raakt geen jobs die op dit moment al draaien (${queueStats.active}). Geannuleerde items kun je later opnieuw queueen via "Start backfill".`,
        );
        if (!isConfirmed) {
            return;
        }
        setIsClearing(true);
        setError(null);
        try {
            const result = (await openApiFetchPost('/api/ai/backfill/cancel-pending', {}, { clientCompanyId })) as { cancelled: number };
            await refreshQueueStats(clientCompanyId);
            setLastStartResult(null);
            setError(`${result.cancelled} job${result.cancelled === 1 ? '' : 's'} geannuleerd.`);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Annuleren mislukt');
        } finally {
            setIsClearing(false);
        }
    }

    async function runPreview() {
        if (!canRun()) {
            return;
        }
        setIsPreviewing(true);
        setError(null);
        setLastStartResult(null);
        try {
            const result = await openApiFetchGet('/api/ai/backfill/preview', { clientCompanyId, startDate, endDate });
            setPreview(result);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Preview mislukt');
            setPreview(null);
        } finally {
            setIsPreviewing(false);
        }
    }

    async function runStart() {
        if (!canRun() || !preview) {
            return;
        }
        const toProcess = (shouldIncludeAssets ? preview.assets.toProcess : 0) + (shouldIncludeSurveys ? preview.surveys.toProcess : 0);
        if (toProcess === 0) {
            setError('Geen documenten of surveys om te verwerken');
            return;
        }
        const isConfirmed = window.confirm(`Weet je zeker dat je ${toProcess} embed-jobs wilt aanmaken?`);
        if (!isConfirmed) {
            return;
        }
        setIsStarting(true);
        setError(null);
        try {
            const result = await openApiFetchPost(
                '/api/ai/backfill/start',
                {},
                {
                    clientCompanyId,
                    startDate,
                    endDate,
                    includeAssets: shouldIncludeAssets,
                    includeSurveys: shouldIncludeSurveys,
                },
            );
            setLastStartResult(result);
            await runPreview();
            await refreshQueueStats(clientCompanyId);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Start mislukt');
        } finally {
            setIsStarting(false);
        }
    }

    return (
        <>
            <TopbarLeftOnly>
                <TopbarCrumbActive title="AI Backfill" icon="fa-certificate" />
            </TopbarLeftOnly>
            <Content>
                <Tray>
                    <Panel>
                        <PanelBody>
                            <div style={{ padding: 15 }}>
                                <div style={{ marginBottom: 15 }}>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 5 }}>Client Company</label>
                                    <select
                                        className="form-control"
                                        value={clientCompanyId}
                                        onChange={(e) => setClientCompanyId(e.target.value)}
                                        style={{ maxWidth: 400 }}
                                    >
                                        <option value="">— Selecteer een client —</option>
                                        {clients.map((c) => (
                                            <option key={c.value} value={c.value}>
                                                {c.text}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div style={{ display: 'flex', gap: 15, marginBottom: 15, flexWrap: 'wrap' }}>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: 600, marginBottom: 5 }}>Startdatum</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: 600, marginBottom: 5 }}>Einddatum</label>
                                        <input type="date" className="form-control" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                                    </div>
                                </div>
                                <div style={{ marginBottom: 15 }}>
                                    <label style={{ marginRight: 15 }}>
                                        <input
                                            type="checkbox"
                                            checked={shouldIncludeAssets}
                                            onChange={(e) => setShouldIncludeAssets(e.target.checked)}
                                        />{' '}
                                        Documenten (assets)
                                    </label>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={shouldIncludeSurveys}
                                            onChange={(e) => setShouldIncludeSurveys(e.target.checked)}
                                        />{' '}
                                        Surveys (summaryAndAdvice)
                                    </label>
                                </div>
                                <div style={{ marginBottom: 15 }}>
                                    <button
                                        className="btn btn-default"
                                        onClick={() => void runPreview()}
                                        disabled={!canRun() || isPreviewing || isStarting}
                                    >
                                        {isPreviewing ? 'Bezig...' : 'Preview count'}
                                    </button>{' '}
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => void runStart()}
                                        disabled={!canRun() || !preview || isPreviewing || isStarting}
                                    >
                                        {isStarting ? 'Bezig...' : 'Start backfill'}
                                    </button>
                                </div>
                                {error && <div style={{ color: '#dc3545', marginBottom: 10 }}>{error}</div>}
                                {(isPreviewing || isStarting) && <Spinner />}
                                {preview && !isPreviewing && (
                                    <div style={{ marginTop: 15 }}>
                                        {shouldIncludeAssets && <CountsBlock title="Documenten" counts={preview.assets} />}
                                        {shouldIncludeSurveys && <CountsBlock title="Surveys" counts={preview.surveys} />}
                                    </div>
                                )}
                                {lastStartResult && (
                                    <div style={{ marginTop: 15, padding: 10, background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: 4 }}>
                                        Gequeued: {lastStartResult.assetsQueued} documenten, {lastStartResult.surveysQueued} surveys.
                                        {(lastStartResult.assetsSkipped > 0 || lastStartResult.surveysSkipped > 0) && (
                                            <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
                                                Overgeslagen (al in queue): {lastStartResult.assetsSkipped} documenten,{' '}
                                                {lastStartResult.surveysSkipped} surveys.
                                            </div>
                                        )}
                                    </div>
                                )}
                                {queueStats && clientCompanyId && (
                                    <div
                                        style={{
                                            marginTop: 20,
                                            padding: 12,
                                            border: '1px solid #ddd',
                                            borderRadius: 4,
                                            background: '#fafafa',
                                            maxWidth: 600,
                                        }}
                                    >
                                        <div style={{ fontWeight: 600, marginBottom: 6 }}>Wachtrij voor deze client</div>
                                        <div style={{ fontSize: 13, color: '#555' }}>
                                            Wachtend: <strong>{queueStats.pending}</strong> · Bezig: <strong>{queueStats.active}</strong> · Hertry:{' '}
                                            <strong>{queueStats.retry}</strong>
                                        </div>
                                        {queueStats.pending + queueStats.retry > 0 && (
                                            <div style={{ marginTop: 10 }}>
                                                <button
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => void runCancelPending()}
                                                    disabled={isClearing || isStarting}
                                                >
                                                    {isClearing ? 'Bezig...' : `Wis wachtrij (${queueStats.pending + queueStats.retry})`}
                                                </button>
                                                <span style={{ marginLeft: 10, fontSize: 12, color: '#888' }}>
                                                    Annuleert alleen wachtende jobs — al lopende jobs gaan door.
                                                </span>
                                            </div>
                                        )}
                                        <div style={{ marginTop: 8 }}>
                                            <button
                                                className="btn btn-link btn-sm"
                                                style={{ padding: 0 }}
                                                onClick={() => void refreshQueueStats(clientCompanyId)}
                                            >
                                                <i className="fa fa-refresh" /> Vernieuw
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </PanelBody>
                    </Panel>
                </Tray>
            </Content>
        </>
    );
}
